import React, { FC, useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  Select,
  SelectOption,
  Switch,
  Textfield,
  ValidationMessage,
} from '@innovationnorway/cl-react';
import {
  DevelopmentProjectDto,
  DevelopmentProjectListItemDto,
  EventCategory,
  EventCreateDtoV2,
  EventDtoV2,
  EventUpdateDtoV2,
  OperationProjectDto,
  PortalProjectDto,
  PortalProjectListItemDto,
  ProjectListItemDto,
  ProjectsDto,
} from 'src/api/v2';
import { requiredI18n } from 'src/utils/validation';

import { useTranslation } from 'react-i18next';
import { convertHoursAndMinutesToHours, convertHourstoHoursAndMinutes } from 'src/utils/time';
import AddEntityModalButton from 'src/components/AddEntityModalButton/AddEntityModalButton';
import DeleteEventForm from './DeleteEventForm';
import { formatDateInputDate } from 'src/utils/FormatValue';
import { getSafeDateOnly } from 'src/utils/date';

interface EventFormProps {
  onSubmit: (data: ExtendedCreateEventDto | ExtendedUpdateEventDto) => void;
  onClose?: () => void;
  setSelectedProjectId: (id: string) => void;
  project?:
    | DevelopmentProjectDto
    | PortalProjectDto
    | OperationProjectDto
    | ProjectsDto
    | DevelopmentProjectListItemDto
    | PortalProjectListItemDto
    | ProjectListItemDto;
  defaultValues: Partial<EventDtoV2 & { projectId?: string }>;
  showProjectSelector: boolean;
  showExpenseAssociation: boolean;
  showDeleteFormButton: boolean;
  shouldUseDefaults: boolean;
  isLoading: boolean;
  projectOptions: SelectOptionItem[];
  projectExpenseOptions: SelectOptionItem[];
  projectStartDate?: Date;
  projectEndDate?: Date;
  activityStartDate?: Date;
  activityEndDate?: Date;
}

export type SelectOptionItem = { value: string; text: string };

export interface ExtraFormDto {
  formHours: number;
  formMinutes: number;
  projectId: string;
  expenseTypeIds: string[];
  category: EventCategory;
}
type ExtendedCreateEventDto = EventCreateDtoV2 & ExtraFormDto;
type ExtendedUpdateEventDto = EventUpdateDtoV2 & ExtraFormDto;
const EventForm: FC<EventFormProps> = ({
  onSubmit,
  onClose,
  setSelectedProjectId,
  project,
  defaultValues,
  showProjectSelector,
  showExpenseAssociation,
  showDeleteFormButton,
  shouldUseDefaults,
  projectOptions,
  projectExpenseOptions,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { t: tEvents } = useTranslation('events');
  const { t: tProjects } = useTranslation('projects');
  const { t: tError } = useTranslation('error');
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    watch,
    getValues,
  } = useForm<ExtendedCreateEventDto | ExtendedUpdateEventDto>(
    shouldUseDefaults ? { defaultValues } : undefined,
  );
  const values = getValues();
  const projectSelectId = useId();
  const expenseSelectId = useId();
  const [isClosed, setIsClosed] = useState<boolean>(!values.isPublic);

  const minuteWatch = watch('formMinutes', 0);
  const hourWatch = watch('formHours', 0);

  function ensureInt(n: number) {
    const parsed = parseInt(n + '');
    return parsed || 0;
  }

  const { hours, minutes } = convertHourstoHoursAndMinutes(defaultValues?.duration || values.duration);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="stack-medium">
        {showProjectSelector && (
          <Controller
            control={control}
            name="projectId"
            rules={{
              validate: {
                assertNotEmpty: (value) =>
                  value != null && value !== ' ' ? true : requiredI18n(tError).message,
              },
            }}
            render={({ field, fieldState }) => (
              <div>
                <label htmlFor={projectSelectId} className="font-weight--semi-bold margin-bottom--1">
                  {tProjects('project')}
                </label>
                <Select
                  id={projectSelectId}
                  value={field.value ?? ''}
                  name={field.name}
                  onChange={(event) => {
                    field.onChange(event.target.value);
                    setSelectedProjectId(event.target.value);
                    setValue('expenseTypeIds', []);
                  }}
                >
                  <SelectOption value="" disabled>
                    {`${t('select')} ${tProjects('project').toLowerCase()}`}
                  </SelectOption>
                  {projectOptions.map((option) => (
                    <SelectOption key={option.value} value={option.value}>
                      {option.text}
                    </SelectOption>
                  ))}
                </Select>
                {fieldState.error?.message && (
                  <ValidationMessage>{fieldState.error.message}</ValidationMessage>
                )}
              </div>
            )}
          />
        )}

        <Controller
          control={control}
          name="name"
          rules={{ required: requiredI18n(tError) }}
          render={({ field, fieldState }) => (
            <Textfield label={tEvents('eventName')} {...field} error={fieldState.error?.message} />
          )}
        />
        <div className="display--flex gap--2">
          <Textfield
            className="flex--1"
            type="date"
            name={
              register('startDate', {
                required: requiredI18n(tError),
              }).name
            }
            label={t('startDate')}
            min={project ? formatDateInputDate(getSafeDateOnly(new Date(project.startDate))) : undefined}
            max={project ? formatDateInputDate(getSafeDateOnly(new Date(project.endDate))) : undefined}
            onChange={(event) => {
              const { value } = event.target;
              const StartDate = value
                ? getSafeDateOnly(new Date(value))
                : project
                  ? getSafeDateOnly(new Date(project.startDate))
                  : getSafeDateOnly(new Date());
              setValue('startDate', StartDate, { shouldValidate: true, shouldDirty: true });
            }}
            value={
              values.startDate ? formatDateInputDate(getSafeDateOnly(new Date(values.startDate))) : undefined
            }
            error={errors.startDate?.message}
          />
          <Textfield
            className="flex--1"
            type="date"
            name={register('endDate').name}
            label={`${t('endDate')} (${t('optional')})`}
            min={project ? formatDateInputDate(getSafeDateOnly(new Date(project.startDate))) : undefined}
            max={project ? formatDateInputDate(getSafeDateOnly(new Date(project.endDate))) : undefined}
            onChange={(event) => {
              const { value } = event.target;
              const EndDate = value
                ? getSafeDateOnly(new Date(value))
                : project
                  ? getSafeDateOnly(new Date(project.endDate))
                  : getSafeDateOnly(new Date());
              setValue('endDate', EndDate, { shouldValidate: true, shouldDirty: true });
            }}
            value={
              values.endDate ? formatDateInputDate(getSafeDateOnly(new Date(values.endDate))) : undefined
            }
            error={errors.endDate?.message}
          />
        </div>
        <Switch
          label={tEvents('closedEvent')}
          checked={isClosed}
          onChange={(event) => {
            setIsClosed(event.target.checked);
            setValue('isPublic', event.target.checked);
          }}
        />
        <Textfield
          multiline
          label={t('description')}
          {...register('description', {
            required: requiredI18n(tError),
          })}
          error={errors?.description?.message}
        />
        <div className="display--flex gap--2">
          <Controller
            control={control}
            name="location"
            rules={{ required: requiredI18n(tError) }}
            render={({ field, fieldState }) => (
              <Textfield
                className="flex--1"
                value={field.value}
                label={tEvents('location')}
                name={field.name}
                onChange={(value) => {
                  field.onChange(value);
                }}
                error={fieldState.error?.message}
              />
            )}
          />
        </div>
        <div className="margin-bottom--1 font-weight--semi-bold font-size--large">
          {tEvents('expectedDuration')}
        </div>

        <div className="display--flex gap--2">
          <Textfield
            className="flex--1"
            aria-label={`${tEvents('expectedDuration')} ${tEvents('hours')}`}
            type="number"
            suffix={tEvents('hours')}
            {...register(`formHours`, {
              required: values.formMinutes === 0 ? requiredI18n(tError) : false,
              validate:
                minutes === 0
                  ? {
                      assertTotalNotZero: (value) =>
                        convertHoursAndMinutesToHours(ensureInt(value), ensureInt(minuteWatch)) > 0
                          ? true
                          : requiredI18n(tError).message,
                    }
                  : undefined,
            })}
            defaultValue={hours}
            error={errors.formHours?.message}
          />

          <Textfield
            className="flex--1"
            aria-label={`${tEvents('expectedDuration')} ${tEvents('minutes')}`}
            aria-describedby={'hoursAndMinutesLabel'}
            type="number"
            suffix={tEvents('minutes')}
            {...register(`formMinutes`, {
              required: values.formHours === 0 ? requiredI18n(tError) : false,
              validate:
                hours === 0
                  ? {
                      assertTotalNotZero: (value) =>
                        convertHoursAndMinutesToHours(ensureInt(hourWatch), ensureInt(value)) > 0
                          ? true
                          : requiredI18n(tError).message,
                    }
                  : undefined,
            })}
            defaultValue={minutes}
            error={errors.formMinutes?.message}
          />
        </div>
        {showExpenseAssociation && (
          <Controller
            control={control}
            name="expenseTypeIds"
            rules={{ required: requiredI18n(tError) }}
            render={({ field, fieldState }) => {
              return (
                <div>
                  <label htmlFor={expenseSelectId} className="font-weight--semi-bold margin-bottom--1">
                    {tEvents('associateToType')}
                  </label>
                  <Select
                    id={expenseSelectId}
                    name={field.name}
                    value={field.value?.[0] ?? ''}
                    onChange={(event) => {
                      field.onChange([event.target.value]);
                    }}
                  >
                    <SelectOption value="" disabled>
                      {`${t('select')} ${t('costType')}`}
                    </SelectOption>
                    {projectExpenseOptions.map((option) => (
                      <SelectOption key={option.value} value={option.value}>
                        {option.text}
                      </SelectOption>
                    ))}
                  </Select>
                  {fieldState.error?.message && (
                    <ValidationMessage>{fieldState.error.message}</ValidationMessage>
                  )}
                </div>
              );
            }}
          />
        )}
      </div>
      <div className="display--flex gap--2 justify-content--space-between padding-top--3">
        <div>
          <Button type="submit" loading={isLoading} className="margin-right--4">
            {t('save')}
          </Button>
          <Button variant="secondary" type="button" onClick={onClose} disabled={isLoading}>
            {t('cancel')}
          </Button>
        </div>

        {showDeleteFormButton && (
          <div>
            <AddEntityModalButton
              buttonText={t('delete')}
              buttonColor="danger"
              modalTitle={`${t('delete')} ${t('event').toLowerCase()}`}
            >
              <DeleteEventForm event={defaultValues as EventDtoV2} projectId={defaultValues?.projectId} />
            </AddEntityModalButton>
          </div>
        )}
      </div>
    </form>
  );
};

export default EventForm;
